import Vue from 'vue';
import createAuth0Client from '@auth0/auth0-spa-js';
import * as Sentry from '@sentry/vue';

// code in sync with help/docs/.vuepress/auth.js

const DEFAULT_REDIRECT_CALLBACK = () => window.history.replaceState({}, document.title, window.location.pathname);

let instance;

export const getInstance = () => instance;

export const useAuth0 = ({
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  redirectUri = window.location.origin,
  ...options
}) => {
  if (instance) return instance;

  instance = new Vue({
    data() {
      return {
        loading: true,
        is_authenticated: false,
        user: {},
        auth0Client: null,
        popupOpen: false,
        error: null,
      };
    },

    async created() {
      const { audience, client_id, domain } = options;

      const scope = 'openid email profile';
      const auth0_local_storage_key = `@@auth0spajs@@::${client_id}::${audience}::${scope}`;
      const is_cypress = localStorage.getItem(auth0_local_storage_key) !== null;

      const args = {
        audience,
        client_id,
        domain,
        redirect_uri: redirectUri,
        useRefreshTokens: !is_cypress,
        cacheLocation: 'localstorage',
      };

      this.auth0Client = await createAuth0Client(args);
      await this.checkSession();

      try {
        if (
          window.location.search.includes('code=')
          && window.location.search.includes('state=')
        ) {
          const { appState } = await this.handleRedirectCallback();
          this.error = null;
          onRedirectCallback(appState);
        }
      } catch (e) {
        this.error = e;
      } finally {
        this.is_authenticated = await this.auth0Client.isAuthenticated();
        if (this.is_authenticated) {
          this.user = await this.auth0Client.getUser();
        } else {
          this.user = null;
        }
        this.loading = false;
      }
    },
    methods: {
      async handleRedirectCallback() {
        this.loading = true;
        try {
          await this.auth0Client.handleRedirectCallback();
          this.user = await this.auth0Client.getUser();
          this.is_authenticated = true;
          this.error = null;
        } catch (e) {
          this.error = e;
        } finally {
          this.loading = false;
        }
      },

      async loginWithRedirect() {
        const ziggu_base_url = window.location.origin;

        // retrieve and append any (present) ziggu parameters to pass onto auth0 login page
        // We can't use URLSearchParams because it's not supported on IE
        const targetUrl = window.location.pathname;
        const params = _.chain(window.location.search)
          .replace('?', '') // remove starting ?
          .split('&')
          .map(_.partial(_.split, _, '=', 2))
          .fromPairs()
          .value();
        const ziggu_email_address = decodeURIComponent(params.ziggu_email_address || '');
        // TODO: can we differentiate clean slate login vs session expired (LOGIN_PAGE_NOTIFICATIONS.SESSION_EXPIRED)
        const ziggu_notification = decodeURIComponent(params.ziggu_notification || 'login');

        await this.auth0Client.loginWithRedirect({
          appState: { targetUrl, params },
          ziggu_base_url,
          ziggu_email_address,
          ziggu_notification,
        });
      },

      async getToken(o) {
        let token;
        try {
          token = localStorage.getItem('_jwt');
          if (token) return token;

          token = await this.auth0Client.getTokenSilently(o);
        } catch (error) {
          // skip logging expected errors to sentry
          if (error && error.error !== 'login_required') Sentry.captureException(error);
        } finally {
          // failed silent authentication (error or no token): redirect to universal login
          if (!token) await this.loginWithRedirect();
        }
        return token;
      },

      getUser() {
        return this.auth0Client.getUser();
      },

      logout(o) {
        return this.auth0Client.logout(o);
      },

      async checkSession() {
        // invalidate local application session, re-authenticate via auth0 session:
        // - enforces single logout on (re)load: logout on a single (sub)domain will trigger logout on all (sub)domains
        // - enforcing single logout on route navigation: done via user integrity
        await this.auth0Client.checkSession({ ignoreCache: true });
      },
    },
  });

  return instance;
};

/* eslint-disable no-shadow,no-param-reassign,camelcase */

export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  },
};
